import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useChat } from "@ai-sdk/react";
import { useQuery } from "@tanstack/react-query";
import {
  AnythinkChatTransport,
  AIUsageInfo,
  DEFAULT_CHAT_MODEL,
} from "@/lib/anythinkChatTransport";
import {
  chatPersistenceService,
  ChatConversation,
} from "@/services/breeze/ChatPersistenceService";
import { BaseService } from "@/services/BaseService";

// Fetch AI config and models from tenant settings
class AiConfigService extends BaseService {
  getConfig = () => this.getRequest<AiConfig>("/integrations/ai/config");
  getModels = () => this.getRequest<ModelOption[]>("/integrations/ai/models");
}
const aiConfigService = new AiConfigService();

export interface AiConfig {
  ai_mode: "platform" | "byok";
  ai_byok_provider?: string;
  ai_default_model?: string;
}

export interface ModelOption {
  id: string;
  name: string;
  badge: string;
  provider: string;
  isByok: boolean;
  multiplier?: number;
}

const SIDEBAR_AGENT_ID = "sidebar";
const LS_ACTIVE_CONVERSATION = "anythink_sidebar_conversation";

interface UseSidebarChatOptions {
  defaultModel?: string;
}

export function useSidebarChat(options: UseSidebarChatOptions = {}) {
  // Fetch tenant AI config
  const { data: aiConfig } = useQuery({
    queryKey: ["ai-config"],
    queryFn: () => aiConfigService.getConfig(),
    staleTime: 60_000, // Cache for 1 minute
  });

  const aiMode = aiConfig?.ai_mode || "platform";

  // Fetch available models dynamically from billing API (via AnyApi)
  const { data: discoveredModels } = useQuery({
    queryKey: ["ai-models", aiMode],
    queryFn: async () => {
      const rawModels = await aiConfigService.getModels();
      // Map from API format (AiModelMultiplier) to ModelOption format
      return rawModels.map((m: any) => ({
        id: m.modelName || m.model_name,
        name: m.modelName || m.model_name,
        provider: m.provider,
        badge: m.badge,
        isByok: m.isByok ?? m.is_byok,
        multiplier: m.multiplier,
      }));
    },
    staleTime: 5 * 60_000, // Cache for 5 minutes
  });

  // Use discovered models filtered by AI mode
  const availableModels: ModelOption[] = useMemo(() => {
    if (discoveredModels) {
      return discoveredModels.filter((m) =>
        aiMode === "byok" ? m.isByok : !m.isByok,
      );
    }
    return []; // No fallback if user wants ONLY models from there
  }, [discoveredModels, aiMode]);

  const { defaultModel } = options;
  const resolvedDefaultModel =
    defaultModel ||
    aiConfig?.ai_default_model ||
    availableModels[0]?.id || // Pick the "fast" one
    DEFAULT_CHAT_MODEL;

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    number | null
  >(() => {
    const stored = localStorage.getItem(LS_ACTIVE_CONVERSATION);
    return stored ? parseInt(stored, 10) : null;
  });
  const [selectedModel, setSelectedModel] = useState(resolvedDefaultModel);

  // Sync selected model when config changes (e.g. user changes default in settings)
  const prevDefaultModel = useRef(resolvedDefaultModel);
  useEffect(() => {
    if (prevDefaultModel.current !== resolvedDefaultModel) {
      setSelectedModel(resolvedDefaultModel);
      prevDefaultModel.current = resolvedDefaultModel;
    }
  }, [resolvedDefaultModel]);

  // Ensure selected model is valid for the current mode
  useEffect(() => {
    if (
      availableModels.length > 0 &&
      !availableModels.some((m) => m.id === selectedModel)
    ) {
      setSelectedModel(availableModels[0].id);
    }
  }, [availableModels, selectedModel]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const persistedMessageIds = useRef(new Set<string>());
  const [sessionUsage, setSessionUsage] = useState<AIUsageInfo>({
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    is_byok: false,
  });

  const transport = useMemo(() => {
    const t = new AnythinkChatTransport();
    t.onUsage = (usage) => {
      setSessionUsage((prev) => ({
        input_tokens: prev.input_tokens + usage.input_tokens,
        output_tokens: prev.output_tokens + usage.output_tokens,
        total_tokens: prev.total_tokens + usage.total_tokens,
        model: usage.model,
        is_byok: usage.is_byok,
      }));
    };
    return t;
  }, []);

  const {
    messages,
    sendMessage: rawSendMessage,
    status,
    error,
    clearError,
    setMessages,
  } = useChat({
    transport: transport as any,
    id: "anythink-sidebar", // Fixed ID — don't change per conversation
    onError: (err) => console.error("Chat error:", err),
    experimental_throttle: 50,
  });

  const isLoading = status === "streaming" || status === "submitted";

  // Persist active conversation ID to localStorage
  useEffect(() => {
    if (activeConversationId) {
      localStorage.setItem(
        LS_ACTIVE_CONVERSATION,
        String(activeConversationId),
      );
    } else {
      localStorage.removeItem(LS_ACTIVE_CONVERSATION);
    }
  }, [activeConversationId]);

  // Refresh conversation list
  const loadConversations = useCallback(async () => {
    try {
      const convos =
        await chatPersistenceService.getConversations(SIDEBAR_AGENT_ID);
      setConversations(
        convos.sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        ),
      );
    } catch (err) {
      console.error("[SidebarChat] Failed to refresh conversations:", err);
    }
  }, []);

  // Load on mount — restore last conversation or select most recent
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        const convos =
          await chatPersistenceService.getConversations(SIDEBAR_AGENT_ID);
        const sorted = convos.sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
        setConversations(sorted);

        if (sorted.length === 0) return;

        // Try to restore the saved conversation, or fall back to most recent
        const savedId = activeConversationId;
        const targetId =
          savedId && sorted.some((c) => c.id === savedId)
            ? savedId
            : sorted[0].id;

        await loadMessages(targetId);
      } catch (err) {
        console.error("[SidebarChat] Failed to initialize:", err);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load messages for a conversation
  const loadMessages = useCallback(
    async (conversationId: number) => {
      setIsLoadingHistory(true);
      try {
        const conversation =
          await chatPersistenceService.getConversation(conversationId);
        setActiveConversationId(conversationId);

        if (conversation.messages && conversation.messages.length > 0) {
          const uiMessages = conversation.messages.map((msg, idx) => ({
            id: `persisted-${msg.id}-${idx}`,
            role: msg.role as "user" | "assistant",
            content: msg.content,
            parts: [{ type: "text" as const, text: msg.content }],
            createdAt: new Date(msg.created_at),
          }));
          setMessages(uiMessages);
          persistedMessageIds.current = new Set(uiMessages.map((m) => m.id));
        } else {
          setMessages([]);
          persistedMessageIds.current.clear();
        }
      } catch (err) {
        console.error("[SidebarChat] Failed to load messages:", err);
        setMessages([]);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [setMessages],
  );

  // Select a conversation
  const selectConversation = useCallback(
    async (conversationId: number) => {
      if (conversationId === activeConversationId) return;
      setShowHistory(false);
      await loadMessages(conversationId);
    },
    [activeConversationId, loadMessages],
  );

  // Start a new conversation
  const newConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    persistedMessageIds.current.clear();
    clearError();
    setShowHistory(false);
  }, [setMessages, clearError]);

  // Store attachments per message for rendering thumbnails
  const messageAttachments = useRef<
    Map<
      string,
      Array<{ mediaType: string; base64Data: string; fileName: string }>
    >
  >(new Map());

  // Send message with persistence
  const sendMessage = useCallback(
    async (
      text: string,
      attachments?: Array<{
        mediaType: string;
        base64Data: string;
        fileName: string;
      }>,
    ) => {
      if (
        (!text.trim() && (!attachments || attachments.length === 0)) ||
        isLoading
      )
        return;

      clearError();
      let conversationId = activeConversationId;

      // Create conversation on first message
      if (!conversationId) {
        try {
          const convo = await chatPersistenceService.createConversation({
            name: text.slice(0, 50) + (text.length > 50 ? "..." : ""),
            agentId: SIDEBAR_AGENT_ID,
          });
          conversationId = convo.id;
          setActiveConversationId(conversationId);
          loadConversations();
        } catch (err) {
          console.error("[SidebarChat] Failed to create conversation:", err);
        }
      }

      // Persist user message
      if (conversationId) {
        try {
          await chatPersistenceService.createMessage(conversationId, {
            content: text,
            role: "user",
          });
        } catch (err) {
          console.error("[SidebarChat] Failed to persist user message:", err);
        }
      }

      // Store attachments for rendering in the message bubble
      if (attachments && attachments.length > 0) {
        messageAttachments.current.set(text, attachments);
      }

      // Send to AI — rawSendMessage adds it to the UI and starts streaming
      rawSendMessage({ text }, { body: { model: selectedModel, attachments } });
    },
    [
      isLoading,
      activeConversationId,
      selectedModel,
      rawSendMessage,
      clearError,
      loadConversations,
    ],
  );

  // Persist assistant response when streaming completes
  const prevStatus = useRef(status);
  useEffect(() => {
    if (
      prevStatus.current === "streaming" &&
      status === "ready" &&
      activeConversationId
    ) {
      const lastAssistant = [...messages]
        .reverse()
        .find(
          (m) =>
            m.role === "assistant" && !persistedMessageIds.current.has(m.id),
        );

      if (lastAssistant) {
        const text =
          lastAssistant.parts
            ?.filter(
              (p): p is { type: "text"; text: string } => p.type === "text",
            )
            .map((p) => p.text)
            .join("") ?? "";

        if (text) {
          persistedMessageIds.current.add(lastAssistant.id);
          chatPersistenceService
            .createMessage(activeConversationId, {
              content: text,
              role: "assistant",
            })
            .catch((err) =>
              console.error(
                "[SidebarChat] Failed to persist assistant message:",
                err,
              ),
            );

          loadConversations();
        }
      }
    }
    prevStatus.current = status;
  }, [status, messages, activeConversationId, loadConversations]);

  // Delete a conversation
  const deleteConversation = useCallback(
    async (conversationId: number) => {
      try {
        await chatPersistenceService.deleteConversation(conversationId);
        if (activeConversationId === conversationId) {
          newConversation();
        }
        loadConversations();
      } catch (err) {
        console.error("[SidebarChat] Failed to delete conversation:", err);
      }
    },
    [activeConversationId, newConversation, loadConversations],
  );

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    clearError,
    status,
    selectedModel,
    setSelectedModel,
    availableModels,
    aiMode,
    messageAttachments: messageAttachments.current,
    conversations,
    activeConversationId,
    selectConversation,
    newConversation,
    deleteConversation,
    isLoadingHistory,
    showHistory,
    setShowHistory,
    sessionUsage,
  };
}
